import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import {
  usePlan,
  useUpdatePlan,
  useDeletePlan,
  useGoals,
  useActivities,
  useCreateActivity,
} from "@/hooks/useCareer";
import { Modal } from "@/components/ui/modal";
import {
  EntityRow,
  ProgressBar,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import { isOpenWork, suggestedProgressFromWork } from "@/lib/goals-progress";

export function PlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const planQuery = usePlan(planId);
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();
  const goalsQuery = useGoals();
  const activitiesQuery = useActivities(false);
  const createActivity = useCreateActivity();

  const plan = planQuery.data?.plan;
  const goals = goalsQuery.data?.goals ?? [];
  const parentGoal = goals.find((g) => g.id === plan?.goalId);
  const planWork = useMemo(
    () => (activitiesQuery.data?.activities ?? []).filter((a) => a.planId === planId),
    [activitiesQuery.data?.activities, planId],
  );
  const suggested = suggestedProgressFromWork(planWork);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [kind, setKind] = useState("search");
  const [status, setStatus] = useState("active");
  const [goalId, setGoalId] = useState("");
  const [timeHorizon, setTimeHorizon] = useState("");
  const [rationale, setRationale] = useState("");
  const [reviewCadence, setReviewCadence] = useState("");

  const [workOpen, setWorkOpen] = useState(false);
  const [workTitle, setWorkTitle] = useState("");

  useEffect(() => {
    if (!plan) return;
    setTitle(plan.title);
    setObjective(plan.primaryObjective);
    setKind(plan.kind);
    setStatus(plan.status);
    setGoalId(plan.goalId ?? "");
    setTimeHorizon(plan.timeHorizon ?? "");
    setRationale(plan.rationale ?? "");
    setReviewCadence(plan.reviewCadence ?? "");
  }, [plan]);

  if (planQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div>
        <Link to="/plans" className="text-sm text-brand hover:underline">
          Back to Plans
        </Link>
        <p className="mt-4 text-sm text-destructive">Plan not found.</p>
      </div>
    );
  }

  async function save() {
    if (!planId) return;
    await updatePlan.mutateAsync({
      id: planId,
      input: {
        title: title.trim(),
        primaryObjective: objective.trim(),
        kind,
        status,
        goalId: goalId || null,
        timeHorizon: timeHorizon.trim() || null,
        rationale: rationale.trim() || null,
        reviewCadence: reviewCadence.trim() || null,
      },
    });
    setEditing(false);
  }

  async function markReviewed() {
    if (!planId) return;
    await updatePlan.mutateAsync({
      id: planId,
      input: { lastReviewedAt: new Date().toISOString() },
    });
  }

  async function handleDelete() {
    if (!planId || !confirm("Delete this plan?")) return;
    await deletePlan.mutateAsync(planId);
    navigate("/plans");
  }

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <Link to="/plans" className="mb-4 inline-flex text-sm text-brand hover:underline">
        Back to Plans
      </Link>

      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{plan.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <StatusChip>{plan.kind}</StatusChip>
              <span>{plan.status}</span>
              {plan.timeHorizon ? <span>{plan.timeHorizon}</span> : null}
              {parentGoal ? (
                <Link to={`/goals/${parentGoal.id}`} className="text-brand hover:underline">
                  {parentGoal.title}
                </Link>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/ai-assistant?planId=${encodeURIComponent(plan.id)}`}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Sparkles className="size-3.5" />
              Ask AI
            </Link>
            <button
              type="button"
              onClick={() => void markReviewed()}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
            >
              Mark reviewed
            </button>
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
            >
              {editing ? "Cancel" : "Edit"}
            </button>
          </div>
        </div>
      </header>

      {editing ? (
        <section className="mb-8 space-y-3 rounded-lg border border-border bg-card p-4">
          <input className={fieldClassName()} value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea
            className={fieldClassName()}
            rows={3}
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
          />
          <textarea
            className={fieldClassName()}
            rows={2}
            placeholder="Rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <select className={fieldClassName()} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="search">Search</option>
              <option value="networking">Networking</option>
              <option value="negotiation">Negotiation</option>
              <option value="direction">Direction</option>
              <option value="onboarding">Onboarding</option>
            </select>
            <select className={fieldClassName()} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="abandoned">Abandoned</option>
            </select>
            <input
              className={fieldClassName()}
              placeholder="Time horizon"
              value={timeHorizon}
              onChange={(e) => setTimeHorizon(e.target.value)}
            />
            <input
              className={fieldClassName()}
              placeholder="Review cadence"
              value={reviewCadence}
              onChange={(e) => setReviewCadence(e.target.value)}
            />
            <select className={fieldClassName()} value={goalId} onChange={(e) => setGoalId(e.target.value)}>
              <option value="">No linked goal</option>
              {goals.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-between">
            <button type="button" onClick={() => void handleDelete()} className="text-xs text-destructive">
              Delete plan
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={updatePlan.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Primary objective
            </h3>
            <p className="mt-2 text-sm">{plan.primaryObjective}</p>
          </section>
          {plan.rationale ? (
            <section className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rationale
              </h3>
              <p className="mt-2 text-sm">{plan.rationale}</p>
            </section>
          ) : null}
          <section className="mb-8 text-xs text-muted-foreground">
            {plan.reviewCadence ? <p>Cadence: {plan.reviewCadence}</p> : null}
            {plan.lastReviewedAt ? (
              <p>Last reviewed {new Date(plan.lastReviewedAt).toLocaleString()}</p>
            ) : (
              <p>Not reviewed yet</p>
            )}
            {suggested != null ? (
              <div className="mt-3">
                <p className="mb-1">From work ~{suggested}%</p>
                <ProgressBar value={suggested} />
              </div>
            ) : null}
          </section>
        </>
      )}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Work under this plan
          </h3>
          <button
            type="button"
            onClick={() => setWorkOpen(true)}
            className="text-xs text-brand hover:underline"
          >
            Add work
          </button>
        </div>
        {planWork.length === 0 ? (
          <p className="text-sm text-muted-foreground">No work linked yet.</p>
        ) : (
          <ul className="space-y-2">
            {planWork.map((a) => (
              <EntityRow
                key={a.id}
                to={`/work/${a.id}`}
                title={a.title}
                meta={
                  <span>
                    {a.state}
                    {isOpenWork(a.state) ? " · open" : ""}
                  </span>
                }
              />
            ))}
          </ul>
        )}
      </section>

      <Modal open={workOpen} onClose={() => setWorkOpen(false)} labelledBy="plan-add-work">
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (!planId || !workTitle.trim()) return;
            void createActivity
              .mutateAsync({
                title: workTitle.trim(),
                type: "work",
                planId,
                goalId: plan.goalId,
              })
              .then(({ activity }) => {
                setWorkOpen(false);
                setWorkTitle("");
                navigate(`/work/${activity.id}`);
              });
          }}
        >
          <div className="border-b border-border px-4 py-3">
            <h2 id="plan-add-work" className="text-base font-semibold">
              Add work
            </h2>
          </div>
          <div className="px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Work title"
              value={workTitle}
              onChange={(e) => setWorkTitle(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button type="button" onClick={() => setWorkOpen(false)} className="text-sm text-muted-foreground">
              Cancel
            </button>
            <button type="submit" className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground">
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
